// TrendPeak_Analysis.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getRajasthanDailyPeak,
  getRajasthanMonthlyPeakDiscom,
  getDailyDiscomDrawalSchedule,
  getMonthlyDiscomDrawalSchedule,
  getDailyTotalDrawalSchedule,
  getMonthlyTotalDrawalSchedule,
  getRajasthanDailyPeakForecast,
  getRajasthanMonthlyPeakForecast,
} from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
const X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const GROUP_BY = [
  "Discom Load",
  "Total Discom Load",
  "RVPN Drawal",
  "Total Load & RVPN Drawal",
];
const DISCOM = [
  {
    label: "JDVVNL",
    key: "JDVVNL",
  },
  {
    label: "JVVNL",
    key: "JVVNL",
  },
  {
    label: "AVVNL",
    key: "AVVNL",
  },
];
const TYPE = ["Schedule", "Drawal"];
const FORECAST_TYPES = ["Actual", "Forecast D+1", "Forecast D+2"];
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

// Helper function to get month with year
function getMonthWithYear(month: number, year: number): string {
  if (month === undefined || year === undefined) return "";
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (month < 1 || month > 12) return "";
  const yearShort = year.toString().slice(-2);
  return `${monthNames[month - 1]}-${yearShort}`;
}

// Helper function to parse month-year string back to comparable values
function parseMonthYear(monthYearStr: string | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) {
    return { month: 0, year: 0 };
  }
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parts = monthYearStr.split("-");
  if (parts.length !== 2) {
    return { month: 0, year: 0 };
  }
  const [monthStr, yearStr] = parts;
  const month = monthNames.indexOf(monthStr) + 1;
  const year = 2000 + parseInt(yearStr);
  return { month, year };
}

// Helper to parse "MM-YYYY" format from API to "Mon-YY" format
function parseApiMonthYear(apiMonthYear: string | undefined): string {
  if (!apiMonthYear) {
    return "";
  }
  const parts = apiMonthYear.split("-");
  if (parts.length !== 2) {
    return "";
  }
  const [month, year] = parts;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthIndex = parseInt(month) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    return "";
  }
  const yearShort = year.slice(-2);
  return `${monthNames[monthIndex]}-${yearShort}`;
}

// Helper function to format discom names for display
function formatDiscomName(discom: string): string {
  if (discom === "total_discom") {
    return "Total Discom";
  } else if (discom === "rvpn") {
    return "RVPN";
  }
  return discom.toUpperCase();
}

// Helper function to format key names for display
function formatKeyName(key: string): string {
  // Handle special cases
  if (key === "total_discom") {
    return "Total Discom";
  } else if (key === "rvpn") {
    return "RVPN";
  }

  // For other keys, capitalize first letter and replace underscores
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

interface RajasthanPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
  selectedForecastType?: string[];
  metrics: TrendData | null;
}

export function RajasthanPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  selectedForecastType: propsForecastType,
  metrics,
}: RajasthanPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const [rvpnActualData, setRvpnActualData] = useState<any[]>([]);
  const [rvpnForecastData, setRvpnForecastData] = useState<any[]>([]);
  const [totalDiscomData, setTotalDiscomData] = useState<any[]>([]);
  const [discomLoadData, setDiscomLoadData] = useState<any[]>([]);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const dispatch = useDispatch();

  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [selectedForecastType, setSelectedForecastType] = useState<string[]>(
    propsForecastType || ["Actual"],
  );

  // Sync forecast type from props when it changes
  useEffect(() => {
    if (propsForecastType && propsForecastType.length > 0) {
      setSelectedForecastType(propsForecastType);
    }
  }, [propsForecastType]);

  const createChartObject = useCallback(
    (
      key: string,
      index: number,
      suffix: string,
      displayName: string,
      stackSuffix: string,
    ) => {
      return {
        dataKey: `${key}_${suffix}`,
        name: `${formatKeyName(key)} ${displayName}`,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? stackSuffix : undefined,
      };
    },
    [selectedChartType],
  );

  const _handlefetchData = useCallback(async () => {
    setLoading(true);

    try {
      const trendDates = DefaultDates[activeTab]?.defaultTrendDates;
      if (!trendDates || trendDates.length === 0) {
        setRvpnActualData([]);
        setRvpnForecastData([]);
        setTotalDiscomData([]);
        setDiscomLoadData([]);
        setLoading(false);
        return;
      }

      // Get first and last date from trend dates
      const allDates = trendDates.flatMap((dateRange: any) => {
        const [start, end] = dateRange.value.split(",");
        return [start, end].filter(Boolean);
      });

      if (allDates.length === 0) {
        setRvpnActualData([]);
        setRvpnForecastData([]);
        setTotalDiscomData([]);
        setDiscomLoadData([]);
        setLoading(false);
        return;
      }

      const sortedDates = allDates.sort(
        (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      );
      const startDateStr = sortedDates[0];
      const endDateStr = sortedDates[sortedDates.length - 1];

      const isMonthly = selectedXaxis.key === "month";

      const [rvpnActualRes, rvpnForecastRes, totalDiscomRes, discomLoadRes] =
        await Promise.all([
          isMonthly
            ? getRajasthanMonthlyPeakDiscom(startDateStr, endDateStr)
            : getRajasthanDailyPeak(startDateStr, endDateStr),
          isMonthly
            ? getRajasthanMonthlyPeakForecast(startDateStr, endDateStr)
            : getRajasthanDailyPeakForecast(startDateStr, endDateStr),
          isMonthly
            ? getMonthlyTotalDrawalSchedule(startDateStr, endDateStr)
            : getDailyTotalDrawalSchedule(startDateStr, endDateStr),
          isMonthly
            ? getMonthlyDiscomDrawalSchedule(startDateStr, endDateStr)
            : getDailyDiscomDrawalSchedule(startDateStr, endDateStr),
        ]);

      setRvpnActualData(Array.isArray(rvpnActualRes) ? rvpnActualRes : []);
      setRvpnForecastData(
        Array.isArray(rvpnForecastRes) ? rvpnForecastRes : [],
      );
      setTotalDiscomData(
        Array.isArray(totalDiscomRes)
          ? totalDiscomRes
          : (totalDiscomRes as any)?.data || [],
      );
      setDiscomLoadData(Array.isArray(discomLoadRes) ? discomLoadRes : []);
    } catch (error) {
      console.error("Error in data fetching:", error);
      setRvpnActualData([]);
      setRvpnForecastData([]);
      setTotalDiscomData([]);
      setDiscomLoadData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  useEffect(() => {
    if (loading) return;

    const isMonthly = selectedXaxis.key === "month";
    const trendDates = DefaultDates[activeTab]?.defaultTrendDates || [];
    const isActualDatesSelected = trendDates.some(
      (d: any) => !d.label?.includes("(Forecast)"),
    );
    const isForecastDatesSelected =
      trendDates.some((d: any) => d.label?.includes("(Forecast)")) ||
      selectedForecastType.some((t: string) => t.includes("Forecast"));

    let allData: any[] = [];

    if (selectedGroupKeys === "RVPN Drawal") {
      let actualData: any[] = [];
      if (selectedForecastType.includes("Actual") && isActualDatesSelected) {
        actualData = rvpnActualData.map((item) => ({
          ...item,
          month: isMonthly
            ? getMonthWithYear(item.month, item.year)
            : undefined,
          forecast_type: "Actual",
        }));
      }

      let forecastData: any[] = [];
      if (
        selectedForecastType.some((t) => t.includes("Forecast")) ||
        isForecastDatesSelected
      ) {
        forecastData = rvpnForecastData
          .filter((item) => {
            if (
              selectedForecastType.includes("Forecast D+1") &&
              item.forecast_type === "T+1"
            )
              return true;
            if (
              selectedForecastType.includes("Forecast D+2") &&
              item.forecast_type === "T+2"
            )
              return true;
            return false;
          })
          .map((item) => ({
            ...item,
            month: isMonthly
              ? getMonthWithYear(item.month, item.year)
              : undefined,
            peak_mw: isMonthly
              ? item.peak_drawal_mw || 0
              : item.daily_peak_drawal_mw || 0,
            morning_peak_mw: isMonthly
              ? item.morning_peak_drawal_mw || 0
              : item.formatted_data?.find(
                (fd: any) => fd.time_period === "Morning",
              )?.max_drawal || 0,
            evening_peak_mw: isMonthly
              ? item.evening_peak_drawal_mw || 0
              : item.formatted_data?.find(
                (fd: any) => fd.time_period === "Evening",
              )?.max_drawal || 0,
            average_mw: isMonthly
              ? item.average_drawal_mw || 0
              : item.avg_daily_drawal_mw || 0,
            forecast_type:
              item.forecast_type === "T+1" ? "Forecast D+1" : "Forecast D+2",
          }));
      }
      allData = [...actualData, ...forecastData];
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const processedTotal = totalDiscomData.map((item) => ({
        ...item,
        month: isMonthly ? getMonthWithYear(item.month, item.year) : undefined,
        discom: "total_discom",
        peak_drawal_mw: isMonthly
          ? item.peak_drawal_mw || 0
          : item.daily_peak_drawal_mw || 0,
        morning_peak_drawal_mw: isMonthly
          ? item.morning_peak_drawal_mw || 0
          : item.formatted_data?.find((fd: any) => fd.time_period === "Morning")
            ?.max_drawal || 0,
        evening_peak_drawal_mw: isMonthly
          ? item.evening_peak_drawal_mw || 0
          : item.formatted_data?.find((fd: any) => fd.time_period === "Evening")
            ?.max_drawal || 0,
        average_drawal_mw: isMonthly
          ? item.average_drawal_mw || 0
          : item.avg_daily_drawal_mw || 0,
      }));

      let rvpnData: any[] = [];
      if (selectedForecastType.includes("Actual") && isActualDatesSelected) {
        rvpnData = [
          ...rvpnData,
          ...rvpnActualData.map((item) => ({
            ...item,
            month: isMonthly
              ? getMonthWithYear(item.month, item.year)
              : undefined,
            discom: "rvpn",
            forecast_type: "Actual",
            peak_drawal_mw: item.peak_mw || 0,
            morning_peak_drawal_mw: item.morning_peak_mw || 0,
            evening_peak_drawal_mw: item.evening_peak_mw || 0,
            average_drawal_mw: item.average_mw || 0,
          })),
        ];
      }
      if (
        selectedForecastType.some((t) => t.includes("Forecast")) ||
        isForecastDatesSelected
      ) {
        rvpnData = [
          ...rvpnData,
          ...rvpnForecastData
            .filter((item) => {
              if (
                selectedForecastType.includes("Forecast D+1") &&
                item.forecast_type === "T+1"
              )
                return true;
              if (
                selectedForecastType.includes("Forecast D+2") &&
                item.forecast_type === "T+2"
              )
                return true;
              return false;
            })
            .map((item) => ({
              ...item,
              month: isMonthly
                ? getMonthWithYear(item.month, item.year)
                : undefined,
              discom: "rvpn",
              peak_drawal_mw: isMonthly
                ? item.peak_drawal_mw || 0
                : item.daily_peak_drawal_mw || 0,
              morning_peak_drawal_mw: isMonthly
                ? item.morning_peak_drawal_mw || 0
                : item.formatted_data?.find(
                  (fd: any) => fd.time_period === "Morning",
                )?.max_drawal || 0,
              evening_peak_drawal_mw: isMonthly
                ? item.evening_peak_drawal_mw || 0
                : item.formatted_data?.find(
                  (fd: any) => fd.time_period === "Evening",
                )?.max_drawal || 0,
              average_drawal_mw: isMonthly
                ? item.average_drawal_mw || 0
                : item.avg_daily_drawal_mw || 0,
              forecast_type:
                item.forecast_type === "T+1" ? "Forecast D+1" : "Forecast D+2",
            })),
        ];
      }
      allData = [...processedTotal, ...rvpnData];
    } else if (selectedGroupKeys === "Total Discom Load") {
      allData = totalDiscomData.map((item) => {
        const morning = item.formatted_data?.find(
          (fd: any) => fd.time_period === "Morning",
        );
        const evening = item.formatted_data?.find(
          (fd: any) => fd.time_period === "Evening",
        );
        return {
          ...item,
          month: isMonthly
            ? getMonthWithYear(item.month, item.year)
            : undefined,
          peak_schedule_mw: isMonthly
            ? item.peak_schedule_mw || 0
            : item.daily_peak_schedule_mw || 0,
          peak_drawal_mw: isMonthly
            ? item.peak_drawal_mw || 0
            : item.daily_peak_drawal_mw || 0,
          morning_peak_schedule_mw: isMonthly
            ? item.morning_peak_schedule_mw || 0
            : morning?.max_schedule || 0,
          morning_peak_drawal_mw: isMonthly
            ? item.morning_peak_drawal_mw || 0
            : morning?.max_drawal || 0,
          evening_peak_schedule_mw: isMonthly
            ? item.evening_peak_schedule_mw || 0
            : evening?.max_schedule || 0,
          evening_peak_drawal_mw: isMonthly
            ? item.evening_peak_drawal_mw || 0
            : evening?.max_drawal || 0,
          average_schedule_mw: isMonthly
            ? item.average_schedule_mw || 0
            : item.avg_daily_schedule_mw || 0,
          average_drawal_mw: isMonthly
            ? item.average_drawal_mw || 0
            : item.avg_daily_drawal_mw || 0,
        };
      });
    } else {
      // Discom Load
      allData = discomLoadData.map((item) => ({
        ...item,
        month: isMonthly ? getMonthWithYear(item.month, item.year) : undefined,
        peak_schedule_mw: isMonthly
          ? item.peak_schedule_mw || 0
          : item.daily_peak_schedule_mw || 0,
        peak_drawal_mw: isMonthly
          ? item.peak_drawal_mw || 0
          : item.daily_peak_drawal_mw || 0,
        average_schedule_mw: isMonthly
          ? item.average_schedule_mw || 0
          : item.avg_daily_schedule_mw || 0,
        average_drawal_mw: isMonthly
          ? item.average_drawal_mw || 0
          : item.avg_daily_drawal_mw || 0,
      }));
    }

    if (allData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setUniqueDate([]);
      return;
    }

    let newData = allData;
    if (selectedGroupKeys !== "Discom Load") {
      const dataMap = new Map();
      allData.forEach((item) => {
        const discomPart = item.discom ? `_${item.discom}` : "";
        const forecastPart = item.forecast_type ? `_${item.forecast_type}` : "";
        const uniqueKey = isMonthly
          ? `${item.month}_${item.year}${discomPart}${forecastPart}`
          : `${item.date}${discomPart}${forecastPart}`;
        if (!dataMap.has(uniqueKey)) dataMap.set(uniqueKey, item);
      });
      newData = Array.from(dataMap.values());
    }

    setUniqueDate(
      Array.from(
        new Set(newData.map((item) => item.date).filter(Boolean)),
      ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
    );

    let filteredData = newData;

    // Apply Discom filter only for "Discom Load" group
    if (selectedGroupKeys === "Discom Load") {
      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        filteredData = filteredData.filter((item) =>
          selectedDiscom.includes(item.discom),
        );
      }
    }

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );
    }

    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "month") {
        if (!a.month || !b.month) {
          return 0;
        }
        const aMonthYear = parseMonthYear(a.month);
        const bMonthYear = parseMonthYear(b.month);
        if (aMonthYear.year !== bMonthYear.year) {
          return aMonthYear.year - bMonthYear.year;
        }
        return aMonthYear.month - bMonthYear.month;
      } else {
        if (!a.date || !b.date) {
          return 0;
        }
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;
    const convertedData = filteredData.map((item) => {
      return {
        ...item,
        peak_mw: item.peak_mw ? item.peak_mw * conversionFactor : 0,
        morning_peak_mw: item.morning_peak_mw
          ? item.morning_peak_mw * conversionFactor
          : 0,
        evening_peak_mw: item.evening_peak_mw
          ? item.evening_peak_mw * conversionFactor
          : 0,
        average_mw: item.average_mw
          ? item.average_mw * conversionFactor
          : undefined,
        peak_schedule_mw: item.peak_schedule_mw
          ? item.peak_schedule_mw * conversionFactor
          : 0,
        peak_drawal_mw: item.peak_drawal_mw
          ? item.peak_drawal_mw * conversionFactor
          : 0,
        morning_peak_schedule_mw: item.morning_peak_schedule_mw
          ? item.morning_peak_schedule_mw * conversionFactor
          : 0,
        morning_peak_drawal_mw: item.morning_peak_drawal_mw
          ? item.morning_peak_drawal_mw * conversionFactor
          : 0,
        evening_peak_schedule_mw: item.evening_peak_schedule_mw
          ? item.evening_peak_schedule_mw * conversionFactor
          : 0,
        evening_peak_drawal_mw: item.evening_peak_drawal_mw
          ? item.evening_peak_drawal_mw * conversionFactor
          : 0,
        average_schedule_mw: item.average_schedule_mw
          ? item.average_schedule_mw * conversionFactor
          : 0,
        average_drawal_mw: item.average_drawal_mw
          ? item.average_drawal_mw * conversionFactor
          : 0,
      };
    });

    let aggregationKeys: string[] = [];
    let groupByKeys: string[] = [];

    if (selectedGroupKeys === "RVPN Drawal") {
      aggregationKeys = ["peak_mw", "morning_peak_mw", "evening_peak_mw"];
      groupByKeys = selectedForecastType.length > 1 ? ["forecast_type"] : [];
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      // Only drawal fields for Total Load & RVPN Drawal
      aggregationKeys = [
        "peak_drawal_mw",
        "morning_peak_drawal_mw",
        "evening_peak_drawal_mw",
        "average_drawal_mw",
      ];
      groupByKeys = ["discom"];
    } else if (
      selectedGroupKeys === "Discom Load" ||
      selectedGroupKeys === "Total Discom Load"
    ) {
      aggregationKeys = [
        "peak_schedule_mw",
        "peak_drawal_mw",
        "morning_peak_schedule_mw",
        "morning_peak_drawal_mw",
        "evening_peak_schedule_mw",
        "evening_peak_drawal_mw",
        "average_schedule_mw",
        "average_drawal_mw",
      ];
      groupByKeys = selectedGroupKeys === "Discom Load" ? ["discom"] : [];
    }

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: groupByKeys,
      volumeKey: ``,
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: ["date"],
      priceKey: "",
      data: convertedData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    rvpnActualData,
    rvpnForecastData,
    totalDiscomData,
    discomLoadData,
    selectedXaxis,
    selectedUnit,
    selectedDate,
    selectedDiscom,
    selectedGroupKeys,
    selectedForecastType,
    selectedSuffix,
    chartSuffixes,
    loading,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const legends = useMemo(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      const peakTypes = [
        { suffix: "peak_mw", displayName: "Day Peak" },
        { suffix: "morning_peak_mw", displayName: "Morning Peak" },
        { suffix: "evening_peak_mw", displayName: "Evening Peak" },
      ];

      return peakTypes.flatMap(({ suffix, displayName }) =>
        chartKeys.map((key, index) =>
          createChartObject(key, index, suffix, displayName, suffix),
        ),
      );
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      // For Total Load & RVPN Drawal, only show drawal peaks
      const peakTypes = [
        { field: "peak", displayName: "Day Peak" },
        { field: "morning_peak", displayName: "Morning Peak" },
        { field: "evening_peak", displayName: "Evening Peak" },
      ];

      const legends: any[] = [];

      chartKeys.forEach((key, keyIndex) => {
        const formattedKey = formatDiscomName(key);
        peakTypes.forEach((peak) => {
          const fieldSuffix = `${peak.field}_drawal_mw`;
          const legendDisplayName = `${formattedKey} ${peak.displayName} Drawal`;
          legends.push({
            dataKey: `${key}_${fieldSuffix}`,
            name: legendDisplayName,
            legendColor: undefined,
            stroke: undefined,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal" : undefined,
          });
        });
      });

      return legends;
    } else {
      const peakTypes = [
        { field: "peak", displayName: "Day Peak" },
        { field: "morning_peak", displayName: "Morning Peak" },
        { field: "evening_peak", displayName: "Evening Peak" },
      ];

      const legends: any[] = [];

      chartKeys.forEach((key, keyIndex) => {
        const formattedKey = formatDiscomName(key);
        selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
          peakTypes.forEach((peak) => {
            const fieldSuffix = `${peak.field}_${suffix}_mw`;
            const legendDisplayName = `${formattedKey} ${peak.displayName} ${displayName}`;
            legends.push({
              dataKey: `${key}_${fieldSuffix}`,
              name: legendDisplayName,
              legendColor: undefined,
              stroke: undefined,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            });
          });
        });
      });

      return legends;
    }
  }, [
    selectedGroupKeys,
    chartKeys,
    selectedSuffix,
    selectedChartType,
    createChartObject,
  ]);
  function summaryExtraKeys() {
    if (selectedXaxis.key === "date") {
      return ["date"];
    } else {
      return ["month", "year"];
    }
  }

  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month", "Year"];

  const xDataKey = selectedXaxis.values[0];

  const handleForecastTypeChange = (selected: string[]) => {
    if (selected.length === 0) {
      setSelectedForecastType(["Actual"]);
      return;
    }
    setSelectedForecastType(selected);
    dispatch(setLoadingFilterName(""));
  };

  function getTypes() {
    if (selectedGroupKeys === "RVPN Drawal") {
      return FORECAST_TYPES;
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      // For Total Load & RVPN Drawal, show Drawal and Actual options
      return ["Drawal", "Actual"];
    } else {
      return TYPE;
    }
  }

  function getSelectedTypes() {
    if (selectedGroupKeys === "RVPN Drawal") {
      return selectedForecastType;
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      // For Total Load & RVPN Drawal, always show Drawal, and Actual if selected
      let types = ["Drawal"];
      if (selectedForecastType.includes("Actual")) {
        types.push("Actual");
      }
      return types;
    } else {
      return selectedSuffix.map((item) => item?.displayName || "");
    }
  }
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const commonSummaryProps = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Rajasthan Load Trend Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
            }}
          />
          {selectedGroupKeys !== "RVPN Drawal" &&
            selectedGroupKeys !== "Total Load & RVPN Drawal" && (
              <FilterMenu
                title="Discom Load"
                items={DISCOM.map((item) => item.label)}
                selectedItems={selectedDiscom}
                onSelect={(item, index) => {}}
                setSelectedItems={(item) => {
                  if (
                    selectedGroupKeys === "Total Discom Load" ||
                    selectedGroupKeys === "RVPN Drawal"
                  ) {
                    dispatch(setLoadingFilterName(""));
                    return;
                  }
                  onDiscomChange(item);
                }}
              />
            )}
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              onGroupByChange(index);
            }}
          />

          {selectedGroupKeys === "RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={FORECAST_TYPES}
              selectedItems={selectedForecastType}
              setSelectedItems={handleForecastTypeChange}
            />
          )}

          {/* {selectedGroupKeys !== "Total Load & RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={["Drawal", "Actual"]}
              selectedItems={getSelectedTypes()}
              setSelectedItems={(selected: string[]) => {
                // For Total Load & RVPN Drawal, handle Actual filter
                if (selected.includes("Actual")) {
                  if (!selectedForecastType.includes("Actual")) {
                    setSelectedForecastType(["Actual"]);
                  }
                } else {
                  setSelectedForecastType([]);
                }
                dispatch(setLoadingFilterName(""));
              }}
            />
          )} */}

          {selectedGroupKeys !== "RVPN Drawal" &&
            selectedGroupKeys !== "Total Load & RVPN Drawal" && (
              <FilterMenu
                title="Type"
                items={TYPE}
                selectedItems={selectedSuffix.map(
                  (item) => item?.displayName || "",
                )}
                setSelectedItems={(selected: string[]) => {
                  onTypeChange(selected);
                }}
              />
            )}

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Trend Peak Analysis"
              subtitle="Fetching Rajasthan load trend peak analysis data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-100">
                  Load Trend Peak Analysis ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart") {
                        downloadChartTableCSV({
                          ...commonSummaryProps,
                        });
                      } else if (dataTab.label === "Summary") {
                        downloadSummaryCSV(commonSummaryProps);
                      } else if (dataTab.label === "Table") {
                        downloadChartTableCSV(commonSummaryProps);
                      }
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>
              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  key={`${selectedGroupKeys}-${selectedUnit}-${selectedXaxis.key}-${selectedChartType.value}`}
                  xDataKey={xDataKey}
                  secondXDataKey={undefined}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  DISCOM Energy Reports (RUVITL), Load Shedding Reports (RUVITL)
                </p>
              }
              text="1. Actual Drawal = Restricted Demand
           Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding 
2. Load Shedding has been distributed across time blocks based on the actual duration of load shedding
3. MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
